import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Label } from "@/components/CategoryCard";
import { CATEGORY_ICONS, CATEGORY_TINTS } from "@/lib/categories";
import { useServiceI18n } from "@/lib/serviceI18n";
import {
  Building2, Briefcase, Users, ChevronRight, ChevronLeft, Check, Lock,
  Star, ShieldCheck, CreditCard, Apple, Wallet, Sparkles, MapPin, User,
} from "lucide-react";

const ProgressBar = ({ step, t, customerType }) => {
  const skipCompany = customerType === "particulier";
  const allSteps = [
    { id: 1, label: t("wizard.step_type") },
    { id: 2, label: t("wizard.step_company"), skip: skipCompany },
    { id: 3, label: t("wizard.step_needs") },
    { id: 4, label: t("wizard.step_pref") },
    { id: 5, label: t("wizard.step_match") },
  ];
  const visibleSteps = allSteps.filter((s) => !s.skip);

  return (
    <div className="flex items-center gap-2 md:gap-4" data-testid="wizard-progress">
      {visibleSteps.map((s, i) => {
        const done = step > s.id;
        const active = step === s.id;
        return (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 ${active ? "text-[#1A2B4A]" : "text-slate-400"}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                done ? "bg-[#22C55E] text-white" : active ? "bg-[#F5A623] text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {done ? <Check size={18} /> : i + 1}
              </div>
              <span className="hidden md:inline text-sm font-semibold">{s.label}</span>
            </div>
            {i < visibleSteps.length - 1 && <div className={`flex-1 h-0.5 ${step > s.id ? "bg-[#22C55E]" : "bg-slate-200"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const Onboarding = () => {
  const { t } = useTranslation();
  const { catName, subName, subDiensten } = useServiceI18n();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const RECHTSVORMEN = [
    { id: "Eenmanszaak", label: t("wizard.step1.rv_eenmanszaak"), icon: Briefcase },
    { id: "BV", label: t("wizard.step1.rv_bv"), icon: Building2 },
    { id: "VOF", label: t("wizard.step1.rv_vof"), icon: Users },
    { id: "Stichting", label: t("wizard.step1.rv_stichting"), icon: ShieldCheck },
    { id: "Anders", label: t("wizard.step1.rv_anders"), icon: Sparkles },
  ];

  const BRANCHES = [
    { id: "Marketing & Communicatie", label: t("branches.marketing") },
    { id: "ICT & Software", label: t("branches.it") },
    { id: "Consultancy", label: t("branches.consultancy") },
    { id: "E-commerce", label: t("branches.ecommerce") },
    { id: "Horeca", label: t("branches.hospitality") },
    { id: "Retail", label: t("branches.retail") },
    { id: "Bouw", label: t("branches.construction") },
    { id: "Industrie", label: t("branches.industry") },
    { id: "Zorg", label: t("branches.healthcare") },
    { id: "Creatief", label: t("branches.creative") },
    { id: "Vastgoed", label: t("branches.realestate") },
    { id: "Anders", label: t("branches.other") },
  ];

  const MEDEWERKERS = ["1", "2-5", "6-20", "21-50", "50+"];

  const [categories, setCategories] = useState([]);

  const [data, setData] = useState({
    customer_type: "",
    bedrijfsnaam: "",
    kvk: "",
    rechtsvorm: "",
    branche: "",
    medewerkers: "",
    behoeften: [],
    diensten: [],
    extra_info: "",
    locatie: params.get("locatie") || "",
    budget: "",
    meeting_voorkeur: "online",
    contact_naam: "",
    contact_email: "",
  });

  const [klantId, setKlantId] = useState(params.get("klant_id") || null);
  const [paymentMethod, setPaymentMethod] = useState("ideal");

  useEffect(() => {
    api.get("/service-categories").then((r) => {
      setCategories(r.data);
      // Pre-select diensten if provided in URL
      const preselect = params.get("diensten");
      if (preselect) {
        const ids = preselect.split(",").filter(Boolean);
        setData((d) => ({ ...d, diensten: ids }));
      }
    }).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (params.get("step") === "5" && klantId) setStep(5);
  }, [params, klantId]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleDienst = (id) => {
    setData((d) => ({
      ...d,
      diensten: d.diensten.includes(id) ? d.diensten.filter((x) => x !== id) : [...d.diensten, id],
    }));
  };

  const isParticulier = data.customer_type === "particulier";

  const canNext1 = data.customer_type !== "";
  const canNext2 = data.bedrijfsnaam && data.rechtsvorm && data.branche && data.medewerkers;
  const canNext3 = data.diensten.length > 0;
  const canNext4 = data.locatie && data.contact_naam.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contact_email.trim());

  const goNext = async () => {
    if (step === 1) {
      // Skip business step for particulier
      if (isParticulier) setStep(3);
      else setStep(2);
    } else if (step === 4) {
      setLoading(true);
      try {
        const submitData = { ...data };
        if (isParticulier) {
          submitData.bedrijfsnaam = submitData.contact_naam || "Particulier";
          submitData.branche = "Particulier";
          submitData.rechtsvorm = "Particulier";
          submitData.medewerkers = "1";
        }
        const res = await api.post("/klanten", submitData);
        setKlantId(res.data.id);
        setStep(5);
      } catch {
        alert(t("common.error_generic"));
      } finally { setLoading(false); }
    } else { setStep(step + 1); }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep((s) => {
      // Skip business step backwards for particulier
      if (s === 3 && isParticulier) return 1;
      return Math.max(1, s - 1);
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startPayment = async () => {
    if (!klantId) return;
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout/create", { klant_id: klantId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch { alert(t("common.error_generic")); setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 bg-[#FDFCFB]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
          <ProgressBar step={step} t={t} customerType={data.customer_type} />
          <div className="grid lg:grid-cols-3 gap-8 mt-10">
            <div className="lg:col-span-2">
              {step === 1 && (
                <div className="beecenter-card p-6 md:p-10 animate-fade-up" data-testid="wizard-step-1">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2B4A]">{t("wizard.step0.title")}</h2>
                  <p className="text-slate-600 mt-2">{t("wizard.step0.subtitle")}</p>
                  <div className="mt-7 grid sm:grid-cols-2 gap-4">
                    {[
                      { id: "particulier", icon: User, title: t("wizard.step0.particulier_title"), desc: t("wizard.step0.particulier_desc") },
                      { id: "ondernemer", icon: Building2, title: t("wizard.step0.ondernemer_title"), desc: t("wizard.step0.ondernemer_desc") },
                    ].map((o) => {
                      const Icon = o.icon;
                      const active = data.customer_type === o.id;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => set("customer_type", o.id)}
                          className={`p-5 rounded-2xl border-2 text-left transition-all ${
                            active
                              ? "border-[#F5A623] bg-[#F5A623]/5 shadow-md"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                          data-testid={`tile-customer-type-${o.id}`}
                        >
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                            active ? "bg-[#F5A623] text-white" : "bg-slate-100 text-slate-600"
                          }`}>
                            <Icon size={22} />
                          </div>
                          <h3 className="font-heading font-bold text-[#1A2B4A] mt-4">{o.title}</h3>
                          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{o.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="beecenter-card p-6 md:p-10 animate-fade-up" data-testid="wizard-step-2">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2B4A]">{t("wizard.step1.title")}</h2>
                  <p className="text-slate-600 mt-2">{t("wizard.step1.subtitle")}</p>
                  <div className="mt-7 grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step1.company_name")} *</label>
                      <input type="text" value={data.bedrijfsnaam} onChange={(e) => set("bedrijfsnaam", e.target.value)}
                        placeholder="Bright Solutions B.V." className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                        data-testid="input-bedrijfsnaam" />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step1.kvk")} ({t("common.optional")})</label>
                      <input type="text" value={data.kvk} onChange={(e) => set("kvk", e.target.value)}
                        placeholder="89213456" className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                        data-testid="input-kvk" />
                    </div>
                  </div>
                  <div className="mt-7">
                    <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step1.rechtsvorm")} *</label>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                      {RECHTSVORMEN.map((rv) => {
                        const Icon = rv.icon;
                        const active = data.rechtsvorm === rv.id;
                        return (
                          <button key={rv.id} type="button" onClick={() => set("rechtsvorm", rv.id)}
                            className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 text-center transition-all ${
                              active ? "border-[#F5A623] bg-[#F5A623]/5" : "border-slate-200 hover:border-slate-300 bg-white"
                            }`} data-testid={`tile-rechtsvorm-${rv.id}`}>
                            <Icon size={22} className={active ? "text-[#F5A623]" : "text-slate-500"} />
                            <span className={`text-xs font-semibold ${active ? "text-[#1A2B4A]" : "text-slate-700"}`}>{rv.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-7 grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step1.branche")} *</label>
                      <select value={data.branche} onChange={(e) => set("branche", e.target.value)}
                        className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 bg-white" data-testid="select-branche">
                        <option value="">{t("wizard.step1.branche_select")}</option>
                        {BRANCHES.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step1.medewerkers")} *</label>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {MEDEWERKERS.map((m) => (
                          <button key={m} type="button" onClick={() => set("medewerkers", m)}
                            className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all ${
                              data.medewerkers === m ? "border-[#F5A623] bg-[#F5A623]/10 text-[#1A2B4A]" : "border-slate-200 text-slate-600 hover:border-slate-300 bg-white"
                            }`} data-testid={`btn-medewerkers-${m}`}>
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="beecenter-card p-6 md:p-10 animate-fade-up" data-testid="wizard-step-3">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2B4A]">{t("wizard.step2.title")}</h2>
                  <p className="text-slate-600 mt-2">{t("wizard.step2.subtitle")}</p>

                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F5A623]/10 text-[#1A2B4A] text-xs font-semibold">
                    <Sparkles size={12} className="text-[#F5A623]" />
                    {t("wizard.step2.selected_count", { count: data.diensten.length })}
                  </div>

                  <div className="mt-6 space-y-5">
                    {categories.map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.icon] || Sparkles;
                      const tint = CATEGORY_TINTS[cat.color] || CATEGORY_TINTS.slate;
                      const selectedInCat = cat.subcategories.filter((s) => data.diensten.includes(s.id)).length;
                      return (
                        <details key={cat.id} className="beecenter-card overflow-hidden" open={selectedInCat > 0}>
                          <summary className="p-4 cursor-pointer flex items-center gap-3 select-none hover:bg-slate-50 transition-colors" data-testid={`cat-toggle-${cat.id}`}>
                            <div className={`w-10 h-10 rounded-xl ${tint.bg} ${tint.text} ${tint.border} border flex items-center justify-center flex-shrink-0`}>
                              <Icon size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-heading font-bold text-[#1A2B4A]">{catName(cat)}</div>
                              <div className="text-xs text-slate-500">{cat.subcategories.length} {t("services.count_suffix")}</div>
                            </div>
                            {selectedInCat > 0 && (
                              <span className="bg-[#F5A623] text-white text-xs font-bold rounded-full px-2.5 py-1 min-w-[28px] text-center">
                                {selectedInCat}
                              </span>
                            )}
                            <ChevronRight size={18} className="text-slate-400 transition-transform group-open:rotate-90" />
                          </summary>
                          <div className="p-4 pt-0 grid sm:grid-cols-2 gap-2">
                            {cat.subcategories.map((s) => {
                              const active = data.diensten.includes(s.id);
                              return (
                                <button key={s.id} type="button" onClick={() => toggleDienst(s.id)}
                                  className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                                    active ? "border-[#F5A623] bg-[#F5A623]/5" : "border-slate-200 hover:border-slate-300 bg-white"
                                  }`} data-testid={`dienst-${s.id}`}>
                                  <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${active ? "bg-[#F5A623] text-white" : "border-2 border-slate-300"}`}>
                                    {active && <Check size={12} />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                      <span className={`text-sm font-semibold ${active ? "text-[#1A2B4A]" : "text-slate-700"}`}>{subName(s)}</span>
                                      <div className="flex flex-wrap gap-1">
                                        {s.labels.map((l) => <Label key={l} label={l} />)}
                                      </div>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">{subDiensten(s)}</div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </details>
                      );
                    })}
                  </div>

                  <div className="mt-7">
                    <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step2.extra_label")}</label>
                    <textarea value={data.extra_info} onChange={(e) => set("extra_info", e.target.value)}
                      rows={3} placeholder={t("wizard.step2.extra_placeholder")}
                      className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 resize-none"
                      data-testid="input-extra-info" />
                  </div>
                </div>
              )}
              {step === 4 && (
                <div className="beecenter-card p-6 md:p-10 animate-fade-up" data-testid="wizard-step-4">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2B4A]">{t("wizard.step3.title")}</h2>
                  <p className="text-slate-600 mt-2">{t("wizard.step3.subtitle")}</p>
                  <div className="mt-7 grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step3.location")} *</label>
                      <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 focus-within:border-[#F5A623] focus-within:ring-2 focus-within:ring-[#F5A623]/20">
                        <MapPin size={16} className="text-slate-400" />
                        <input value={data.locatie} onChange={(e) => set("locatie", e.target.value)}
                          placeholder={t("wizard.step3.location_placeholder")} className="w-full bg-transparent outline-none" data-testid="input-locatie" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step3.budget")} ({t("common.optional")})</label>
                      <select value={data.budget} onChange={(e) => set("budget", e.target.value)}
                        className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none bg-white" data-testid="select-budget">
                        <option value="">{t("wizard.step3.budget_none")}</option>
                        <option value="<€250">{t("wizard.step3.budget_lt250")}</option>
                        <option value="€250-€500">€250 - €500</option>
                        <option value="€500-€1000">€500 - €1.000</option>
                        <option value=">€1000">{">"} €1.000</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-7">
                    <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step3.meeting")}</label>
                    <div className="mt-2 grid sm:grid-cols-3 gap-3">
                      {[
                        { id: "online", label: t("wizard.step3.meeting_online"), desc: t("wizard.step3.meeting_online_desc") },
                        { id: "fysiek", label: t("wizard.step3.meeting_physical"), desc: t("wizard.step3.meeting_physical_desc") },
                        { id: "beide", label: t("wizard.step3.meeting_both"), desc: t("wizard.step3.meeting_both_desc") },
                      ].map((o) => {
                        const active = data.meeting_voorkeur === o.id;
                        return (
                          <button key={o.id} type="button" onClick={() => set("meeting_voorkeur", o.id)}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${active ? "border-[#F5A623] bg-[#F5A623]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
                            data-testid={`btn-meeting-${o.id}`}>
                            <div className="font-semibold text-[#1A2B4A]">{o.label}</div>
                            <div className="text-xs text-slate-500 mt-1">{o.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-7 grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step3.your_name")} *</label>
                      <input value={data.contact_naam} onChange={(e) => set("contact_naam", e.target.value)}
                        placeholder="Sander de Vries" required className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20" data-testid="input-contact-naam" />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#1A2B4A]">{t("wizard.step3.your_email")} *</label>
                      <input type="email" value={data.contact_email} onChange={(e) => set("contact_email", e.target.value)}
                        placeholder="you@email.com" required className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20" data-testid="input-contact-email" />
                    </div>
                  </div>
                </div>
              )}
              {step === 5 && (
                <div className="beecenter-card p-6 md:p-10 animate-fade-up" data-testid="wizard-step-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#F5A623]/15 flex items-center justify-center">
                      <Lock className="text-[#F5A623]" size={22} />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2B4A]">{t("wizard.step4.title")}</h2>
                      <p className="text-slate-600 text-sm mt-1">{t("wizard.step4.subtitle")}</p>
                    </div>
                  </div>
                  <div className="mt-8 rounded-xl bg-[#F8FAFC] p-6 flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{t("wizard.step4.one_time")}</div>
                      <div className="font-heading text-4xl font-extrabold text-[#1A2B4A] mt-1">€49</div>
                      <div className="text-xs text-slate-500 mt-1">{t("wizard.step4.vat_garanty")}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map((s) => <Star key={s} size={16} className="fill-[#F5A623] text-[#F5A623]" />)}
                      <span className="ml-1 font-semibold text-[#1A2B4A] text-sm">4.9/5</span>
                    </div>
                  </div>
                  <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3" data-testid="step5-refund-promise">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck size={18} />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-emerald-900 text-sm">{t("wizard.step4.refund_promise_title")}</div>
                      <p className="text-sm text-emerald-800/90 mt-1 leading-relaxed">{t("wizard.step4.refund_promise_desc")}</p>
                    </div>
                  </div>
                  <div className="mt-7">
                    <h3 className="font-heading font-bold text-[#1A2B4A] text-sm uppercase tracking-wider">{t("wizard.step4.method")}</h3>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { id: "ideal", label: "iDEAL", icon: Wallet },
                        { id: "bancontact", label: "Bancontact", icon: CreditCard },
                        { id: "card", label: "Card", icon: CreditCard },
                        { id: "applepay", label: "Apple Pay", icon: Apple },
                      ].map((m) => {
                        const Icon = m.icon;
                        const active = paymentMethod === m.id;
                        return (
                          <button key={m.id} type="button" onClick={() => setPaymentMethod(m.id)}
                            className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                              active ? "border-[#F5A623] bg-[#F5A623]/5" : "border-slate-200 bg-white hover:border-slate-300"
                            }`} data-testid={`pay-method-${m.id}`}>
                            <Icon size={22} className={active ? "text-[#F5A623]" : "text-slate-500"} />
                            <span className="text-xs font-semibold text-[#1A2B4A]">{m.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{t("wizard.step4.stripe_note")}</p>
                  </div>
                  <button onClick={startPayment} disabled={loading || !klantId}
                    className="mt-8 btn-amber w-full !py-4 !text-base disabled:opacity-60 disabled:cursor-not-allowed" data-testid="btn-pay">
                    {loading ? t("wizard.step4.redirecting") : (<><Lock size={18} /> {t("wizard.step4.pay_btn")}</>)}
                  </button>
                  <p className="text-xs text-center text-slate-500 mt-3">
                    {t("wizard.step4.terms_note")} <a href="#" className="underline">{t("register.voorwaarden")}</a>.
                  </p>
                </div>
              )}
              {step < 5 && (
                <div className="mt-6 flex items-center justify-between">
                  <button onClick={goBack} disabled={step === 1}
                    className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1A2B4A] font-semibold disabled:opacity-40" data-testid="wizard-back">
                    <ChevronLeft size={18} /> {t("common.previous")}
                  </button>
                  <button onClick={goNext}
                    disabled={loading || (step === 1 && !canNext1) || (step === 2 && !canNext2) || (step === 3 && !canNext3) || (step === 4 && !canNext4)}
                    className="btn-amber !py-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="wizard-next">
                    {step === 4 ? t("wizard.step3.next_btn") : t("common.next")} <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-6">
              <div className="beecenter-card p-6">
                <h3 className="font-heading font-bold text-[#1A2B4A]">{t("wizard.how_title")}</h3>
                <ul className="mt-4 space-y-3">
                  {[t("wizard.how_1"), t("wizard.how_2"), t("wizard.how_3"), t("wizard.how_4")].map((tx, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check size={18} className="text-[#22C55E] mt-px flex-shrink-0" />
                      <span>{tx}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {step === 5 && (
                <div className="beecenter-card p-6">
                  <h3 className="font-heading font-bold text-[#1A2B4A]">{t("wizard.after_payment_title")}</h3>
                  <ul className="mt-4 space-y-3">
                    {[t("wizard.after_payment_1"), t("wizard.after_payment_2"), t("wizard.after_payment_3"), t("wizard.after_payment_4"), t("wizard.after_payment_5")].map((tx, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check size={18} className="text-[#22C55E] mt-px flex-shrink-0" />
                        <span>{tx}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Onboarding;
